import Dexie, { type EntityTable } from 'dexie';
import { ProjectSchema, type NumberNodeProject } from '@numbernode/contracts';

export type ProjectRecord = { id: string; name: string; updatedAt: string; json: string };
export type FileRecord = { id: string; projectId: string; key: string; bytes: Uint8Array; mime: string };

class Registry extends Dexie {
  projects!: EntityTable<ProjectRecord, 'id'>;
  files!: EntityTable<FileRecord, 'id'>;
  constructor() {
    super('numbernode-registry-v1');
    this.version(1).stores({ projects: 'id, updatedAt, name', files: 'id, projectId, [projectId+key]' });
  }
}

const registry = new Registry();
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function opfsAvailable(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.storage?.getDirectory);
}

async function projectDirectory(projectId: string, create = true): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  const projects = await root.getDirectoryHandle('projects', { create });
  return projects.getDirectoryHandle(projectId, { create });
}

async function writeOpfs(projectId: string, key: string, bytes: Uint8Array): Promise<void> {
  const dir = await projectDirectory(projectId);
  const segments = key.split('/').filter(Boolean);
  const fileName = segments.pop();
  if (!fileName) throw new Error('Invalid storage key.');
  let current = dir;
  for (const segment of segments) current = await current.getDirectoryHandle(segment, { create: true });
  const handle = await current.getFileHandle(fileName, { create: true });
  const writable = await handle.createWritable();
  await writable.write(bytes as BlobPart);
  await writable.close();
}

async function readOpfs(projectId: string, key: string): Promise<Uint8Array | undefined> {
  try {
    let current = await projectDirectory(projectId, false);
    const segments = key.split('/').filter(Boolean);
    const fileName = segments.pop();
    if (!fileName) return undefined;
    for (const segment of segments) current = await current.getDirectoryHandle(segment, { create: false });
    const handle = await current.getFileHandle(fileName, { create: false });
    return new Uint8Array(await (await handle.getFile()).arrayBuffer());
  } catch {
    return undefined;
  }
}

export async function saveProject(project: NumberNodeProject): Promise<void> {
  const validated = ProjectSchema.parse(project);
  const json = JSON.stringify(validated);
  await registry.projects.put({ id: validated.id, name: validated.name, updatedAt: validated.updatedAt, json });
  if (opfsAvailable()) await writeOpfs(validated.id, 'project.json', encoder.encode(json));
}

export async function loadProject(projectId: string): Promise<NumberNodeProject | undefined> {
  if (opfsAvailable()) {
    const bytes = await readOpfs(projectId, 'project.json');
    if (bytes) return ProjectSchema.parse(JSON.parse(decoder.decode(bytes)));
  }
  const record = await registry.projects.get(projectId);
  return record ? ProjectSchema.parse(JSON.parse(record.json)) : undefined;
}

export async function listProjects(): Promise<ProjectRecord[]> {
  return registry.projects.orderBy('updatedAt').reverse().toArray();
}

export async function putProjectFile(projectId: string, key: string, bytes: Uint8Array, mime = 'application/octet-stream'): Promise<void> {
  if (opfsAvailable()) await writeOpfs(projectId, key, bytes);
  await registry.files.put({ id: `${projectId}:${key}`, projectId, key, bytes, mime });
}

export async function getProjectFile(projectId: string, key: string): Promise<Uint8Array | undefined> {
  if (opfsAvailable()) {
    const bytes = await readOpfs(projectId, key);
    if (bytes) return bytes;
  }
  return (await registry.files.get(`${projectId}:${key}`))?.bytes;
}

export async function listProjectFiles(projectId: string): Promise<Array<{ key: string; bytes: Uint8Array; mime: string }>> {
  const rows = await registry.files.where('projectId').equals(projectId).toArray();
  return rows.map(({ key, bytes, mime }) => ({ key, bytes, mime }));
}

export async function storageStatus(): Promise<{ usage?: number; quota?: number; persisted?: boolean; opfs: boolean }> {
  const estimate = await navigator.storage?.estimate?.();
  const persisted = await navigator.storage?.persisted?.();
  return {
    ...(estimate?.usage === undefined ? {} : { usage: estimate.usage }),
    ...(estimate?.quota === undefined ? {} : { quota: estimate.quota }),
    ...(persisted === undefined ? {} : { persisted }),
    opfs: opfsAvailable(),
  };
}

export async function requestPersistence(): Promise<boolean> {
  return (await navigator.storage?.persist?.()) ?? false;
}
